import { useMemo, useState, useEffect } from 'react';

import { useLocation, useParams } from 'react-router';

import { useAbilityContext } from '@/contexts/ability';
import { useWorkspaceContext } from '@/contexts/workspace';

import { getClientProfile } from '@/services/ClientProfile';

import { usePath } from './usePath';
import { usePersistentUserLocalStorage as useUserLocalStorage } from './useUserLocalStorage';

export const useBaseCurrency = () => {
  const { pathObject } = usePath();
  const { workspace } = useParams();
  const actualLocation = useLocation();

  const ability = useAbilityContext();
  const { workspace: { defaultWorkspace } } = useWorkspaceContext();

  const [baseCurrency, setBaseCurrency] = useState('');
  const [showSetCurrency, setShowSetCurrency] = useState(false);
  const [showCurrencySwitch, setShowCurrencySwitch] = useState(false);
  const [currencySelected, setCurrencySelected] = useUserLocalStorage('currencySelected', false);
  const [targetCurrency, setTargetCurrency] = useUserLocalStorage('targetCurrency', null);

  useEffect(() => {
    if (pathObject?.key && defaultWorkspace?.currentUser.role) {
      if (
        ability.can('view', pathObject?.key)
        && [
          `/${workspace}/`,
          `/${workspace}/overview`,
          `/${workspace}/overview/old/accounts`,
          `/${workspace}/overview/old/analytic`,
          `/${workspace}/overview/old/strategic`,
          `/${workspace}/overview/old/optimization`,
          `/${workspace}/accounts`,
          `/${workspace}/investments`,
          `/${workspace}/investments/history`,
          `/${workspace}/accounts/history`,
          `/${workspace}/accounts/forecast`,
          `/${workspace}/cash-flows`,
          `/${workspace}/cash-flows/consolidated`,
        ].includes(actualLocation.pathname)
      ) {
        Promise.all([getClientProfile()])
          .then(([client]) => {
            const currency = client?.baseCurrency || '';
            setBaseCurrency(currency);

            if (currency && !targetCurrency) {
              setTargetCurrency(currency);
            }

            if (currency === '') {
              setCurrencySelected(false);
              setShowCurrencySwitch(false);

              if (ability.can('view', 'settings-company')) {
                setShowSetCurrency(true);
              } else {
                setShowSetCurrency(false);
              }
            } else {
              setShowCurrencySwitch(true);
              setShowSetCurrency(false);
            }
          });
      } else {
        setShowCurrencySwitch(false);
        setShowSetCurrency(false);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathObject, defaultWorkspace]);

  const changeCurrencySelected = () => {
    setCurrencySelected(!currencySelected);
  };

  const changeTargetCurrency = (currency) => {
    setTargetCurrency(currency);
  };

  const value = useMemo(() => ({
    baseCurrency,
    showSetCurrency,
    currencySelected,
    showCurrencySwitch,
    targetCurrency,
    changeCurrencySelected,
    changeTargetCurrency,
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [baseCurrency, showSetCurrency, currencySelected, showCurrencySwitch, targetCurrency]);

  return value;
};
